import { AlertTriangle, BellRing, Play } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { cn } from "../lib/utils.ts";

export interface Transcript {
  id: number;
  actor: string;
  input: string;
  text: string;
  mine: boolean;
  data?: string;
  kind?: "reply" | "cron" | "error";
}

export function TranscriptBubble({ t }: { t: Transcript }) {
  const replay = () => {
    if (!t.data) return;
    const el = new Audio(`data:audio/mp3;base64,${t.data}`);
    el.play().catch((err) => console.error("replay:", err));
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-1 max-w-[80%]",
        t.mine ? "self-end items-end" : "self-start items-start",
      )}
    >
      <span
        className={cn(
          "text-xs uppercase tracking-wider px-2 inline-flex items-center gap-1.5",
          t.kind === "error" ? "text-rose-200" : "opacity-60",
        )}
      >
        {t.kind === "cron" && (
          <BellRing className="h-3 w-3" strokeWidth={2.5} />
        )}
        {t.kind === "error" && (
          <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
        )}
        {t.kind === "cron"
          ? "Reminder"
          : t.kind === "error"
          ? "Agent error"
          : t.actor}
      </span>
      <div
        className={cn(
          "px-5 py-3 rounded-3xl text-lg shadow-lg flex items-start gap-3",
          t.kind === "error"
            ? "bg-rose-500/15 text-rose-50 rounded-bl-md ring-1 ring-rose-400/40"
            : t.kind === "cron"
            ? "bg-amber-400/20 text-amber-50 rounded-bl-md ring-1 ring-amber-300/40"
            : t.mine
            ? "bg-accent text-accent-foreground rounded-br-md"
            : "bg-white/15 text-white rounded-bl-md",
        )}
      >
        <div className="markdown min-w-0 flex-1">
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p className="my-1 first:mt-0 last:mb-0 break-words">
                  {children}
                </p>
              ),
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-white/40 hover:decoration-white"
                >
                  {children}
                </a>
              ),
              code: ({ children, className }) => {
                const isBlock = className?.includes("language-");
                if (isBlock) {
                  return (
                    <code className="block whitespace-pre-wrap bg-black/30 rounded-lg px-3 py-2 my-2 text-sm font-mono">
                      {children}
                    </code>
                  );
                }
                return (
                  <code className="bg-black/25 rounded px-1 py-0.5 text-[0.9em] font-mono">
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre className="my-2">{children}</pre>
              ),
              ul: ({ children }) => (
                <ul className="list-disc ml-5 my-1 space-y-0.5">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal ml-5 my-1 space-y-0.5">
                  {children}
                </ol>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-2 border-white/30 pl-3 my-1 opacity-90">
                  {children}
                </blockquote>
              ),
              h1: ({ children }) => (
                <h1 className="font-bold text-xl my-2 first:mt-0">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="font-bold text-lg my-2 first:mt-0">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="font-bold my-2 first:mt-0">{children}</h3>
              ),
            }}
          >
            {t.text}
          </ReactMarkdown>
        </div>
        {t.data && (
          <button
            onClick={replay}
            aria-label="Replay translation"
            className="shrink-0 grid place-items-center h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors mt-0.5"
          >
            <Play className="h-4 w-4" strokeWidth={2.5} />
          </button>
        )}
      </div>
      {t.input !== t.text && (
        <span className="text-xs opacity-50 italic px-2 max-w-full truncate">
          original: {t.input}
        </span>
      )}
    </div>
  );
}
